import { useEffect, useState } from "react";
import { api, fmtDateTime } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  ShieldOff,
  Copy,
  Check,
  Loader2,
  History,
  KeyRound,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { PasswordInput } from "../../components/PasswordInput";

// Sécurité panel — TOTP 2FA setup / disable + login attempts journal.
// Rate-limiting is server-side only, no UI toggle needed.
export const AdminSecurityCard = () => {
  const { user, refresh } = useAuth();
  const enabled = !!user?.two_factor_enabled;

  const [phase, setPhase] = useState("idle"); // idle | setup | confirm | codes | disabling
  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null); // { secret, qr_png_base64, otpauth_url }
  const [confirmCode, setConfirmCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [copied, setCopied] = useState(false);

  const [disableCurrent, setDisableCurrent] = useState("");
  const [disableCode, setDisableCode] = useState("");

  const beginSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setConfirmCode("");
      setPhase("setup");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de démarrer la configuration");
    } finally {
      setBusy(false);
    }
  };

  const confirmEnable = async () => {
    if (!confirmCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", {
        secret: setupData.secret,
        code: confirmCode.trim(),
      });
      setRecoveryCodes(data.recovery_codes || []);
      setPhase("codes");
      await refresh();
      toast.success("2FA activée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Code invalide");
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText((recoveryCodes || []).join("\n"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      /* no-op */
    }
  };

  const submitDisable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", {
        current_password: disableCurrent,
        code: disableCode.trim(),
      });
      toast.success("2FA désactivée");
      setPhase("idle");
      setDisableCurrent("");
      setDisableCode("");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec — vérifiez le mot de passe et le code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[var(--line)] bg-white p-5 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          {enabled ? (
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
          ) : (
            <ShieldOff className="h-4 w-4 text-neutral-400" />
          )}
          <h2 className="font-display text-lg">Double authentification (2FA)</h2>
        </div>
        <span
          data-testid="admin-2fa-status"
          className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${
            enabled
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-neutral-100 text-neutral-500 border border-[var(--line)]"
          }`}
        >
          {enabled ? "Activée" : "Désactivée"}
        </span>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Ajoute un code à 6 chiffres généré par une application (Google Authenticator, 1Password, Bitwarden…) à chaque connexion. Fortement recommandé si tu partages parfois cet ordinateur ou publies depuis plusieurs lieux.
      </p>

      {!enabled && phase === "idle" && (
        <button
          onClick={beginSetup}
          disabled={busy}
          data-testid="admin-2fa-enable-btn"
          className="btn-red"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Activer la 2FA
        </button>
      )}

      {phase === "setup" && setupData && (
        <div className="space-y-4">
          <div className="text-sm text-neutral-600">
            1. Scanne ce QR code avec ton application d'authentification :
          </div>
          <div className="flex flex-col sm:flex-row gap-5 items-start">
            <img
              src={`data:image/png;base64,${setupData.qr_png_base64}`}
              alt="QR code TOTP"
              className="w-44 h-44 border border-[var(--line)] rounded bg-white p-2"
              data-testid="admin-2fa-qr"
            />
            <div className="text-xs text-neutral-500 space-y-1">
              <div>Ou saisis manuellement la clé :</div>
              <code className="block text-[11px] bg-neutral-100 px-2 py-1.5 rounded font-mono break-all">
                {setupData.secret}
              </code>
              <div>Nom du compte : <em>Rouge Première</em></div>
              <div>Type : Temps (TOTP)</div>
            </div>
          </div>
          <div>
            <div className="text-sm text-neutral-600 mb-2">
              2. Entre le code à 6 chiffres affiché dans l'app pour confirmer :
            </div>
            <div className="flex gap-2">
              <input
                data-testid="admin-2fa-confirm-code"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                maxLength={6}
                className="w-40 px-3 py-2.5 rounded border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)] font-mono text-center tracking-widest"
              />
              <button
                onClick={confirmEnable}
                disabled={busy || confirmCode.length < 6}
                data-testid="admin-2fa-confirm-btn"
                className="btn-red"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Confirmer
              </button>
              <button
                onClick={() => setPhase("idle")}
                className="btn-ghost"
              >
                <X className="h-4 w-4" /> Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {phase === "codes" && recoveryCodes && (
        <div className="space-y-3">
          <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            <div className="font-medium mb-1">Codes de secours — à copier maintenant</div>
            <div>
              Chaque code n'est utilisable qu'une seule fois. Ils te permettent de te connecter si tu perds ton téléphone. Range-les dans un gestionnaire de mots de passe : ils ne seront <strong>plus jamais réaffichés</strong>.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-neutral-50 border border-[var(--line)] rounded p-3">
            {recoveryCodes.map((c) => (
              <div key={c} className="px-2 py-1 bg-white rounded border border-[var(--line)]">
                {c}
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={copyCodes} className="btn-ghost">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copié" : "Copier tous les codes"}
            </button>
            <button onClick={() => { setPhase("idle"); setRecoveryCodes(null); }} className="btn-red">
              J'ai bien enregistré mes codes
            </button>
          </div>
        </div>
      )}

      {enabled && phase === "idle" && (
        <button
          onClick={() => setPhase("disabling")}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 rounded border border-[var(--line-strong)] hover:border-[var(--red)] hover:text-[var(--red)]"
          data-testid="admin-2fa-disable-btn"
        >
          <ShieldOff className="h-3.5 w-3.5" /> Désactiver la 2FA
        </button>
      )}

      {phase === "disabling" && (
        <div className="space-y-3 mt-3">
          <label className="block text-xs uppercase tracking-widest text-neutral-500">
            Mot de passe actuel
          </label>
          <PasswordInput
            testId="admin-2fa-disable-pwd"
            value={disableCurrent}
            onChange={setDisableCurrent}
          />
          <label className="block text-xs uppercase tracking-widest text-neutral-500">
            Code (TOTP à 6 chiffres ou code de secours)
          </label>
          <input
            data-testid="admin-2fa-disable-code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value)}
            className="w-full px-3 py-2.5 rounded border border-[var(--line-strong)] focus:outline-none focus:border-[var(--red)] font-mono"
          />
          <div className="flex gap-2">
            <button onClick={submitDisable} disabled={busy} className="btn-red">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmer la désactivation
            </button>
            <button onClick={() => setPhase("idle")} className="btn-ghost">
              <X className="h-4 w-4" /> Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Login attempts journal — separate card so both sections stay compact.
export const LoginJournalCard = () => {
  const [rows, setRows] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);

  const load = async () => {
    const { data } = await api.get("/auth/login-attempts", {
      params: onlyFailed ? { only_failed: true, limit: 100 } : { limit: 100 },
    });
    setRows(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlyFailed]);

  return (
    <div className="rounded-md border border-[var(--line)] bg-white p-5 md:p-6 mt-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-[var(--red)]" />
          <h2 className="font-display text-lg">Journal des connexions</h2>
        </div>
        <label className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-neutral-500 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyFailed}
            onChange={(e) => setOnlyFailed(e.target.checked)}
            className="accent-[var(--red)]"
          />
          Uniquement échecs
        </label>
      </div>
      {rows === null ? (
        <div className="text-sm text-neutral-400 py-6">Chargement…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-neutral-500 italic py-6">
          Aucune tentative journalisée pour le moment.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-widest text-neutral-400 border-b border-[var(--line)]">
                <th className="pb-2 pr-3">Horodatage</th>
                <th className="pb-2 pr-3">Email</th>
                <th className="pb-2 pr-3">IP</th>
                <th className="pb-2 pr-3">Statut</th>
                <th className="pb-2">Raison</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  data-testid="login-attempt-row"
                  className="border-b border-[var(--line)] last:border-b-0"
                >
                  <td className="py-2 pr-3 whitespace-nowrap text-neutral-500">
                    {fmtDateTime(r.timestamp)}
                  </td>
                  <td className="py-2 pr-3 truncate max-w-[220px]">{r.email}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-neutral-500">{r.ip}</td>
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${
                        r.success
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-red-50 text-[var(--red)] border border-red-200"
                      }`}
                    >
                      {r.success ? "OK" : "Échec"}
                    </span>
                  </td>
                  <td className="py-2 text-xs text-neutral-500">{r.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] uppercase tracking-widest text-neutral-400 mt-3 flex items-center gap-1.5">
        <KeyRound className="h-3 w-3" />
        Rate limit actif : 5 tentatives / 15 min → verrouillage progressif (1min → 5min → 15min → 1h)
      </div>
    </div>
  );
};
